import {
  DueDiligenceFunnelListStatusFilter,
  DueDiligenceFunnelSortBy,
  DueDiligenceFunnelSortDirection,
  DueDiligenceRecordStatus,
  Role,
  Security_Status
} from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { defaultListDataWithId } from '@/components/dealPipeline-page/modify/constants';
import { IListDataWithId } from '@/components/dealPipeline-page/modify/types';
import CreateManager from '@/components/dueDiligence-page/modifyDiligence/modals/createManager';
import CreateStrategy from '@/components/dueDiligence-page/modifyDiligence/modals/createStrategy';
import CreateTag from '@/components/dueDiligence-page/modifyDiligence/modals/createTag';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { LoaderOnWholeScreen } from '@/components/loaders/loader-on-whole-screen';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useGoBack } from '@/hooks/useGoBack';
import { getUTCDate } from '@/shared/utils/date';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  CREATE_DUE_DILIGENCE_RECORD_MUTATION,
  CREATE_INVESTMENT_MUTATION,
  DUE_DILIGENCE_FUNNEL_LIST_QUERY,
  DUE_DILIGENCE_RECORD_QUERY_MIN,
  GET_CURRENT_MANAGERS_QUERY,
  GET_CURRENT_SUB_ASSET_CLASSES_QUERY,
  GET_CURRENT_TAGS_QUERY,
  LIST_USERS_QUERY,
  UPDATE_DUE_DILIGENCE_RECORD_MUTATION,
  UPDATE_INVESTMENT_MUTATION
} from '../queries';
import { DetailsSection } from './sections/details';

export type IDiligenceForm = {
  name: string;
  managerId: string;
  assetClass: string;
  finalClose: string;
  ownerEmail: string;
  funnelId: string;
  contact: string;
  referral: string;
  targetIRR: string;
  targetYield: string;
  vintageYear: string;
  description: string;
};

const ModifyDiligence = () => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { user } = useAuth();
  const goBack = useGoBack();
  const { handleSubmit, control, setValue, reset } = useForm<IDiligenceForm>();

  const [searchParams] = useSearchParams();

  const recordId = searchParams.get('recordId') || '';
  const funnelId = searchParams.get('funnelId') || '';
  const status = searchParams.get('status') || '';
  const backToTitle = searchParams.get('backToTitle') || '';

  const [ownerSelectOptions, setOwnerSelectOptions] = useState<ISelectValue[]>([]);
  const [funnelSelectOptions, setFunnelSelectOptions] = useState<ISelectValue[]>([]);
  const [managerList, setManagerList] = useState<string[]>([]);
  const [tags, setTags] = useState<IListDataWithId>(defaultListDataWithId);
  const [strategies, setStrategies] = useState<IListDataWithId>(defaultListDataWithId);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [createDueDiligenceRecord] = useMutation(CREATE_DUE_DILIGENCE_RECORD_MUTATION);
  const [updateDueDiligenceRecord] = useMutation(UPDATE_DUE_DILIGENCE_RECORD_MUTATION);
  const [createInvestment] = useMutation(CREATE_INVESTMENT_MUTATION);
  const [updateInvestment] = useMutation(UPDATE_INVESTMENT_MUTATION);

  useQuery(GET_CURRENT_MANAGERS_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const sortedManagerList = [...data.getCurrentManagers.values]
        .filter((manager: string) => manager !== '')
        .sort((a: string, b: string) => a.localeCompare(b));
      setManagerList(sortedManagerList);
    }
  });

  const { data: tagsListData } = useQuery(GET_CURRENT_TAGS_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const tagsList = [...data.getCurrentTags.values]
        .filter((strategy: string) => strategy !== '')
        .map((strategy) => createSimpleSelectOption(strategy));
      setTags({ ...tags, allList: tagsList, list: tagsList });
    }
  });

  const { data: strategiesListData } = useQuery(GET_CURRENT_SUB_ASSET_CLASSES_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const strategiesList = [...data.getCurrentSubAssetClasses.values]
        .filter((strategy: string) => strategy !== '')
        .map((strategy) => createSimpleSelectOption(strategy));
      setStrategies({ ...strategies, allList: strategiesList, list: strategiesList });
    }
  });

  const { data: recordData, loading: recordLoading } = useQuery(DUE_DILIGENCE_RECORD_QUERY_MIN, {
    fetchPolicy: 'network-only',
    variables: { id: recordId },
    skip: !recordId
  });

  useEffect(() => {
    if (recordData?.getDueDiligenceRecord) {
      const { funnel, investment, manager, owner } = recordData.getDueDiligenceRecord;
      const { name, finalClose, assetClass, targetIRR, targetYield, vintageYear, contact, referral, description } = investment;
      reset((defaultValues) => ({
        ...defaultValues,
        name,
        funnelId: funnel?.id,
        ownerEmail: owner?.email,
        managerId: manager,
        targetIRR: targetIRR ? `${targetIRR?.toFixed(2)}%` : '',
        targetYield: targetYield ? `${targetYield?.toFixed(2)}%` : '',
        vintageYear: vintageYear?.toString(),
        finalClose: finalClose ? getUTCDate(new Date(finalClose)) : null,
        assetClass,
        referral,
        contact,
        description
      }));
      setTags({ ...tags, selected: investment.tags ?? [] });
      setStrategies({
        ...strategies,
        selected: investment.subAssetClasses ?? []
      });
      return;
    }
    reset((defaultValues) => ({
      ...defaultValues,
      ...(funnelId && { funnelId: funnelId })
    }));
  }, [recordData]);

  useEffect(() => {
    setTags((prevTags) => {
      return {
        ...prevTags,
        list: prevTags.list.filter((tag) => !prevTags.selected.includes(tag.value)).sort((a, b) => a.label.localeCompare(b.label))
      };
    });
  }, [recordData, tagsListData, tags.selected]);

  useEffect(() => {
    setStrategies((prevStrategies) => {
      return {
        ...prevStrategies,
        list: prevStrategies.list
          .filter((strategy) => !prevStrategies.selected.includes(strategy.value))
          .sort((a, b) => a.label.localeCompare(b.label))
      };
    });
  }, [recordData, strategiesListData, strategies.selected]);

  const { loading: userLoading } = useQuery(LIST_USERS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      filters: {
        roles: [Role.InvestmentLead, Role.InvestmentMember, Role.InvestmentTeamDefault],
        tenantId: user.tenant.id
      }
    },
    onCompleted: (data) => {
      const selectOptions = data.listUsers.map((user) => createSimpleSelectOption(user.email, `${user.firstName} ${user.lastName}`));
      setOwnerSelectOptions(selectOptions);
    }
  });

  const { loading: funnelListLoading } = useQuery(DUE_DILIGENCE_FUNNEL_LIST_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      data: {
        statusFilter: [DueDiligenceFunnelListStatusFilter.Published],
        sortDirection: DueDiligenceFunnelSortDirection.Asc,
        sortBy: DueDiligenceFunnelSortBy.Order
      }
    },
    onCompleted: (data) => {
      const selectOptions = data.listDueDiligenceFunnels.map((funnel) => createSimpleSelectOption(funnel.id, funnel.name));
      setFunnelSelectOptions(selectOptions);
    }
  });

  const handleCreateInvestmentAndDueDiligenceRecord = async (data: IDiligenceForm, tags: string[], strategies: string[]) => {
    const { name, managerId, funnelId, ownerEmail, assetClass, finalClose, targetIRR, targetYield, vintageYear, contact, referral, description } =
      data;

    const response = await createInvestment({
      variables: {
        data: {
          name,
          managerId,
          securityStatus: status as Security_Status,
          investmentOwnerEmail: ownerEmail,
          assetClass: assetClass || null,
          finalClose: finalClose || null,
          subAssetClasses: strategies,
          targetIRR: !!targetIRR ? parseFloat(targetIRR) : null,
          targetYield: !!targetYield ? parseFloat(targetYield) : null,
          vintageYear: !!vintageYear ? parseInt(vintageYear) : null,
          tags,
          contact,
          referral,
          description
        }
      }
    });

    await createDueDiligenceRecord({
      variables: {
        data: {
          funnelId,
          manager: managerId || null,
          status: status === 'LEAD' ? DueDiligenceRecordStatus.NotStarted : DueDiligenceRecordStatus.Active,
          investmentId: response.data.createInvestment.id,
          ownerEmail
        }
      }
    });
  };

  const handleUpdateInvestmentAndDueDiligenceRecord = async (data: IDiligenceForm, tags: string[], strategies: string[]) => {
    const { id, securityStatus } = recordData?.getDueDiligenceRecord?.investment || {};
    const { name, managerId, ownerEmail, assetClass, finalClose, targetIRR, targetYield, vintageYear, contact, referral, description } = data;

    await updateInvestment({
      variables: {
        data: {
          id,
          name,
          managerId,
          securityStatus,
          assetClass: assetClass || null,
          finalClose: finalClose || null,
          subAssetClasses: strategies,
          targetIRR: !!targetIRR ? parseFloat(targetIRR) : null,
          targetYield: !!targetYield ? parseFloat(targetYield) : null,
          vintageYear: !!vintageYear ? parseInt(vintageYear) : null,
          tags,
          contact,
          referral,
          description
        }
      }
    });

    await updateDueDiligenceRecord({
      variables: {
        data: {
          id: recordId,
          manager: managerId || null,
          investmentId: id,
          ownerEmail
        }
      }
    });
  };

  const onSubmit = async (data: IDiligenceForm) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const strategiesList = [...strategies.selected].filter((el) => el.length > 0).map((el) => el.trim());
      const tagsList = [...tags.selected].filter((el) => el.length > 0).map((el) => el.trim());

      if (recordId) {
        await handleUpdateInvestmentAndDueDiligenceRecord(data, tagsList, strategiesList);
      } else {
        await handleCreateInvestmentAndDueDiligenceRecord(data, tagsList, strategiesList);
      }

      handleClose();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openModalWindow = (type: string) => {
    setModalWindow({ ...modalWindow, isOpen: true, type });
  };

  const handleClose = () => {
    goBack({ fallBack: '/dueDiligence' });
  };

  if (recordLoading || userLoading || funnelListLoading) {
    return <LoaderOnWholeScreen size={60} />;
  }

  return (
    <>
      {loading && (
        <ModalWrappedContent isOpen={loading} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      {modalWindow.isOpen && modalWindow.type === 'create-manager' && (
        <CreateManager
          isOpen={modalWindow.isOpen}
          managerList={managerList}
          setManagerList={setManagerList}
          setNewManagerId={(newManagerId) => setValue('managerId', newManagerId)}
          onClose={() => setModalWindow({ type: 'create-manager', isOpen: false })}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'create-strategy' && (
        <CreateStrategy
          isOpen={modalWindow.isOpen}
          setStrategies={setStrategies}
          onClose={() => setModalWindow({ type: 'create-strategy', isOpen: false })}
        />
      )}
      {modalWindow.isOpen && modalWindow.type === 'create-tag' && (
        <CreateTag isOpen={modalWindow.isOpen} setTags={setTags} onClose={() => setModalWindow({ type: 'create-tag', isOpen: false })} />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
        <PageTitle title={recordId ? 'Edit Deal' : 'Add New Deal'} />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <PaddingWrap>
          <Form id="diligenceForm" onSubmit={handleSubmit(onSubmit)}>
            <SectionsWrap>
              <DetailsSection
                control={control}
                tags={tags}
                strategies={strategies}
                managerList={managerList}
                ownerSelectOptions={ownerSelectOptions}
                funnelSelectOptions={funnelSelectOptions}
                setTags={setTags}
                setStrategies={setStrategies}
                openModalWindow={openModalWindow}
              />
            </SectionsWrap>
            <FormFooter onCancel={handleClose} disableSaveButton={loading} />
          </Form>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

export default ModifyDiligence;

const Form = styled.form`
  width: 100%;
  padding: 50px 0;
`;

const SectionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
